// Per-chapter output schemas — runtime quantity contract for Family A/B/C calls.
//
// Decision (Session 19AB · AI_INGEST_STRATEGY §3):
//   When the orchestrator calls an LLM to produce content for ONE chapter,
//   the response must satisfy the per-chapter min target from
//   COMPONENT_MANIFEST.perChapterTarget. We can't bake this into the
//   top-level IngestPackageSchema (the optional shape there must allow old
//   data + partial slices), but we CAN bake it into per-chapter output
//   shapes the orchestrator parses against.
//
//   Per-chapter shapes are factories — caller passes the target.min and
//   the schema enforces it via z.refine. On violation, orchestrator
//   re-prompts with stronger instruction ("you produced N, must be ≥M").
//
// Why factories: targets come from manifest at runtime; we don't want
// hard-coded numbers in schema files (would drift from manifest).
//
// Pattern: caller does
//   const schema = perChapterFlashcardsSchema(manifest.perChapterTarget.min);
//   const parsed = schema.safeParse(llmOutput);
//
// Anti-laziness clause: we also accept a SHORTFALL VARIANT — if AI
// genuinely cannot find enough material, it returns
// { items: [...], quantityShortfallReason: "..." }. Orchestrator logs the
// reason but doesn't retry (per ANTI_LAZINESS_RULES). When `requireMin`
// is set, the shortfall path is rejected.
import { z } from 'zod';
import { FlashcardSchema } from './flashcard.js';
import { McqSchema } from './mcq.js';
import { TrueFalseSchema } from './true-false.js';
import { EssaySchema } from './essay.js';
import { GlossaryItemSchema } from './glossary.js';
import { MindmapSchema } from './mindmap.js';
import { ProofDrillSchema } from './proof-drill.js';
import { LectureNoteSchema } from './lecture-note.js';
import { FreeResponseSchema } from './free-response.js';
import { ReadingPassageSchema } from './reading-passage.js';
/** Item array OR an `{items}` envelope (with or without quantityShortfallReason).
 *  Session 19AH bugfix: LLM frequently emits `{ items: [...] }` without the
 *  shortfall reason even when meeting min count. Schema now accepts all three
 *  shapes; extractItems / extractShortfall normalize.
 *
 *  Isomorphism §7.3 Pattern 3: envelope also accepts optional `stageSummary`
 *  for inter-stage concept coverage communication. */
function buildArrayOrShortfall(itemSchema) {
    return z.union([
        z.array(itemSchema),
        z.object({
            items: z.array(itemSchema),
            quantityShortfallReason: z.string().min(1).optional(),
            stageSummary: StageSummarySchema.optional(),
        }),
    ]);
}
/** Helper: derive the count regardless of which union branch we got. */
export function countItems(out) {
    return Array.isArray(out) ? out.length : out.items.length;
}
/** Helper: extract items array regardless of branch. */
export function extractItems(out) {
    return Array.isArray(out) ? out : out.items;
}
/** Helper: extract shortfall reason if present. */
export function extractShortfall(out) {
    return Array.isArray(out) ? null : out.quantityShortfallReason;
}
// ── Isomorphism §7.3 Pattern 3: Inter-stage Summary ────────────────────────
//
// Structured concept-coverage signal emitted by per-chapter stages.
// Orchestrator collects these into ctx.stageSummaries so downstream stages
// can see what concepts were already covered and what was omitted.
/** Per-chapter stage summary — LLM optionally emits this alongside items. */
export const StageSummarySchema = z.object({
    /** conceptIds from the anchor that this stage's items address. */
    conceptsCovered: z.array(z.string()),
    /** Total concept count (may exceed conceptsCovered.length if items
     *  cover concepts not in the anchor). */
    conceptCount: z.number().int().min(0),
    /** Free-form LLM self-assessment — advisory, not validated. */
    qualityNotes: z.string().nullable().optional(),
    /** conceptIds that should have been covered but weren't. */
    omissions: z.array(z.string()),
});
/** Helper: extract stageSummary from envelope, if present. */
export function extractStageSummary(out) {
    if (Array.isArray(out))
        return null;
    return out.stageSummary ?? null;
}
function refineMin(schema, opts) {
    return schema.refine((out) => {
        const count = countItems(out);
        if (count >= opts.min)
            return true;
        if (opts.requireMin)
            return false;
        // shortfall envelope path: accept if reason was provided
        return !Array.isArray(out) && Boolean(out.quantityShortfallReason);
    }, { message: `must produce ≥${opts.min} items (or include quantityShortfallReason)` });
}
// ── Family A · Generative ──────────────────────────────────────────────────
export function perChapterFlashcardsSchema(opts) {
    return refineMin(buildArrayOrShortfall(FlashcardSchema), opts);
}
export function perChapterGlossarySchema(opts) {
    return refineMin(buildArrayOrShortfall(GlossaryItemSchema), opts);
}
export function perChapterMindmapsSchema(opts) {
    return refineMin(buildArrayOrShortfall(MindmapSchema), opts);
}
export function perChapterLectureNotesSchema(opts) {
    return refineMin(buildArrayOrShortfall(LectureNoteSchema), opts);
}
// ── Family B · Discrimination ──────────────────────────────────────────────
export function perChapterMcqsSchema(opts) {
    return refineMin(buildArrayOrShortfall(McqSchema), opts);
}
export function perChapterTrueFalseSchema(opts) {
    return refineMin(buildArrayOrShortfall(TrueFalseSchema), opts);
}
export function perChapterProofDrillsSchema(opts) {
    return refineMin(buildArrayOrShortfall(ProofDrillSchema), opts);
}
// ── Family C · Synthesis ───────────────────────────────────────────────────
export function perChapterEssaysSchema(opts) {
    return refineMin(buildArrayOrShortfall(EssaySchema), opts);
}
export function perChapterFreeResponsesSchema(opts) {
    return refineMin(buildArrayOrShortfall(FreeResponseSchema), opts);
}
// ── Family B · Discrimination (English reading) ────────────────────────────
export function perChapterReadingPassagesSchema(opts) {
    return refineMin(buildArrayOrShortfall(ReadingPassageSchema), opts);
}
//# sourceMappingURL=per-chapter-output.js.map